import React from 'react';
import { getErrorHandler } from "Utils";
import { BRAND, INK, OPERATION } from 'Utils/styleTokens';
import {
  Button,
  Form,
  Spinner
} from 'react-bootstrap';
import {
  BsFillUnlockFill,
  BsLockFill,
  BsExclamationTriangle
} from 'react-icons/bs';
import { uiLabel } from 'Utils';
import { startDecryptJobWithSavedInventory } from './api';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { useHistory } from 'react-router-dom';

/**
 * The "Details" column cell on the mask/encrypt source lists. Renders a clickable
 * "## fields to mask" / "## fields to encrypt/decrypt" line that opens the inventory editor —
 * the same target the (now-removed) gear icon used. When there are no fields, shows a muted "—".
 * `mode` is 'mask' | 'encrypt'.
 */
function DetailsColumn({ dataSourceId, fieldQty, mode = 'mask', prevSearchid }) {
  const history = useHistory();
  const qty = Number(fieldQty) || 0;
  const isEncrypt = mode === 'encrypt';

  if (dataSourceId == null || qty === 0) {
    return <span className='text-muted'>—</span>;
  }

  const onClick = () => {
    const base = isEncrypt ? '/secure/encrypt/inventory/' : '/secure/mask/inventory/';
    history.push(`${base}${encodeURIComponent(dataSourceId)}`, { prevSearchid });
  };

  const label = `${qty.toLocaleString()} ${qty === 1 ? 'field' : 'fields'} to ${isEncrypt ? 'encrypt/decrypt' : 'mask'}`;
  const tooltipText = isEncrypt ? 'Review fields selected for encryption' : 'Review fields selected for masking';

  return (
    <DiscoveryTooltip text={tooltipText} placement='top'>
      <span
        role='button'
        tabIndex={0}
        className='pointer'
        style={{ color: BRAND.blue }}
        data-testid={`details-${dataSourceId}`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {label}
      </span>
    </DiscoveryTooltip>
  );
}


function getSecureDataVis(secured, total, securedLabel, unsecuredLabel, disabled, decryptedCount, securedByOther) {
  const securedColor = BRAND.blue;
  const totalColor = INK[200]; // remaining / unsecured stays neutral grey so the signal reads

  // if (disabled) {
  //   return [
  //     {
  //       angle: 100,
  //       color: '#cecece',
  //       label: unsecuredLabel,
  //       count: total - secured
  //     },
  //     {
  //       angle: 0,
  //       color: securedColor,
  //       label: securedLabel,
  //       count: 0
  //     }
  //   ]
  // }
  const securedAngle = Math.floor((secured / total) * 100);
  const securedByOtherAngle = Math.floor((securedByOther / total) * 100);
  const decryptedAngle = Math.floor(((decryptedCount || 0) / total) * 100);
  const totalAngle = 100 - securedAngle;

  return [
    { 
      angle: securedAngle, 
      color: securedColor, 
      label: securedLabel, 
      count: secured 
    },
    {
      angle: securedByOtherAngle,
      color: INK[300],
      label: 'Secured',
      count: securedByOther
    },
    // {
    //   angle: decryptedAngle,
    //   color: '#FFA500',
    //   label: 'Decrypted',
    //   count: decryptedCount
    // },
    {
      angle: !total && !secured ? 100 : totalAngle,
      color: totalColor,
      label: unsecuredLabel,
      count: total - secured
    }
  ];
  
  // return [
  //   ...secured ? ([{ 
  //     angle: securedAngle, 
  //     color: securedColor, 
  //     label: securedLabel, 
  //     count: secured 
  //   }]) : ([]),
  //   ...decryptedCount ? ([{
  //     angle: decryptedAngle,
  //     color: '#FFA500',
  //     label: 'Decrypted',
  //     count: decryptedCount
  //   }]) : ([]),
  //   {
  //     angle: !total && !secured ? 100 : totalAngle,
  //     color: totalColor,
  //     label: unsecuredLabel,
  //     count: total - secured
  //   }
  // ];
}

/**
 * Single combined donut for the Secure Data Landscape (mirrors the SDL "ALL SOURCES" donut).
 * Slices: Encrypted, Masked, Synthesized (placeholder, always 0) + a grey "Unsecured" slice.
 * `angle` is a relative weight — RadialChart normalises it, so the raw count works as the angle.
 */
function getSecureLandscapeVis({ encryptedCount = 0, maskedCount = 0, totalSources = 0 } = {}) {
  const enc = Math.max(0, Number(encryptedCount) || 0);
  const mask = Math.max(0, Number(maskedCount) || 0);
  const total = Math.max(0, Number(totalSources) || 0);
  // Combined donut can double-count a source secured two ways; "unsecured" is clamped at 0.
  const unsecured = Math.max(0, total - enc - mask);
  return [
    { angle: enc, color: OPERATION.encrypt.color, label: 'Encrypted', count: enc },
    { angle: mask, color: OPERATION.mask.color, label: 'Masked', count: mask },
    { angle: 0, color: OPERATION.synthesize.color, label: 'Synthesized', count: 0 },
    { angle: unsecured, color: INK[200], label: 'Unsecured', count: unsecured },
  ];
}

/** Coverage-gauge fill data: how much of the eligible estate is secured by one method. */
function getCoverageGaugeData(secured, eligible) {
  const s = Math.max(0, Number(secured) || 0);
  const e = Math.max(0, Number(eligible) || 0);
  return { percent: e ? Math.floor((s / e) * 100) : 0, secured: s, eligible: e };
}

const getFetchHandler = (setFetchingData, setError, setData, fetchCall) => {
  let prevArgs;
  const cache = {};
  let data;

  return async (...args) => {
    const handleError = getErrorHandler(setError);

    try {
      if ((args[0] === undefined || args[0] === null) && prevArgs) {
        // for refreshing / polling
        data = await fetchCall(...prevArgs);
      } else {
        setFetchingData && setFetchingData(true);
        data = await fetchCall(...args);
        prevArgs = args;
        cache[args.join(',')] = args;
      }
      if (setData) setData(data);
    } catch(e) {
      console.log('FetchHandler Error: ', e);
      handleError(e);    
    }
    setFetchingData && setFetchingData(false);
    return data;
  }
}
const getAjaxHandler = (setFetchingData, setError, apiFunc, refreshHandler) => async (...args) => {
  const handleError = getErrorHandler(setError);
  let response = {};
  setFetchingData(true);
  console.log('running ajaxHandler');
  try {
    response = await apiFunc(...args);
    console.log('ajaxHandler response', response);
    if (refreshHandler) await refreshHandler();
    console.log('ajaxHandler after refresh');
  } catch(e) {
    handleError(e);
  }

  setFetchingData(false);
  return response;
}

function getEncryptFieldsTableData(selectedRows) {
  const result = [];

  for (const table in selectedRows) {
    for (const field in selectedRows[table]) {
      const fieldData = selectedRows[table][field];
      const dataType = fieldData.dataType;
      const findings = fieldData.findings;
      const item = {
        table: table,
        field: field,
        dataType: dataType,
        findings: findings
      };
      result.push(item);
    }
  }

  return result;
}

/** Entity with highest numeric count in discover findings object; deterministic tie-break by code. */
function topFindingEntity(findings) {
  if (findings == null || findings === '') return '';
  if (typeof findings === 'object' && !Array.isArray(findings)) {
    const entries = Object.entries(findings);
    if (entries.length === 0) return '';
    const scored = entries.map(([code, v]) => [code, Number(v) || 0]);
    scored.sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])));
    return String(scored[0][0]);
  }
  if (Array.isArray(findings)) {
    return findings.length ? String(findings[0]) : '';
  }
  return String(findings);
}

function entityToAlgorithmId(entity) {
  return entity ? String(entity) : '';
}

function recommendedAlgorithmFromFindings(findings) {
  return entityToAlgorithmId(topFindingEntity(findings));
}

/**
 * Value from entity → algorithm map (map values are masking algorithm ids). Case-insensitive entity key match.
 * @returns {string} trimmed algorithm id, or '' if entity missing or not in map
 */
function maskingAlgorithmFromEntityMap(entity, entityAlgorithmMap = {}) {
  if (entity == null || entity === '') return '';
  const map = entityAlgorithmMap && typeof entityAlgorithmMap === 'object' ? entityAlgorithmMap : {};
  const e = String(entity);
  const tryVal = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '');
  let v = tryVal(map[e]);
  if (v) return v;
  const upper = e.toUpperCase();
  v = tryVal(map[upper]);
  if (v) return v;
  for (const [k, val] of Object.entries(map)) {
    if (typeof k === 'string' && k.toUpperCase() === upper) {
      v = tryVal(val);
      if (v) return v;
    }
  }
  return '';
}

/**
 * Default masking algorithm for a sensitive column whose detected entity has no entry in the
 * entity→algorithm map. FPE_EBCDIC is a general-purpose format-/length-/uniqueness-preserving
 * algorithm accepted by both the masking registry and the UI algorithm list, and its output is
 * plain ASCII (A-Z, a-z, 0-9; everything else passes through). FPE_UTF8 was the previous default,
 * but it maps digits and letters across the whole Unicode alphabet, so on a non-Unicode column
 * (SQL Server CHAR/VARCHAR, latin1 MySQL, non-UTF8 Oracle) every value degraded to "????" and
 * unique indexes failed. Without any fallback such columns shipped with no algorithm and were
 * silently dropped by the platform's inventory normalizer (removeColumnsWithoutAlgorithm), so a
 * "sensitive" column was never actually masked. Keep in step with the platform's
 * CATALOG_FALLBACK_ALGORITHM in WorkerCatalogController.
 */
const DEFAULT_MASKING_ALGORITHM = 'FPE_EBCDIC';

/**
 * Recommended masking algorithm = map value for the top finding entity (not the entity code). When
 * the column HAS a detected entity (i.e. it is sensitive) but that entity is not in the map, fall
 * back to {@link DEFAULT_MASKING_ALGORITHM} so nothing sensitive is left unmasked. A column with no
 * detected entity at all is not sensitive and still resolves to '' (no algorithm invented).
 */
function resolveRecommendedMaskingAlgorithm(findings, entityAlgorithmMap = {}) {
  const ent = topFindingEntity(findings);
  if (!ent) return '';
  return maskingAlgorithmFromEntityMap(ent, entityAlgorithmMap) || DEFAULT_MASKING_ALGORITHM;
}

/**
 * Masking inventory row status (badge). Two states only:
 *   'new'     — the field was not present in the loaded (saved) inventory.
 *   'differs' — the field is saved but its current algorithm differs from the recommendation.
 *               An EMPTY recommendation counts: a saved algorithm whose field no longer draws a
 *               recommendation (latest discover found nothing) is out of sync, not in sync.
 *   ''        — saved and in sync with the recommendation (no badge).
 * The recommendation is display-only and is passed in as `recommendedAlgorithm` (already
 * resolved against the provider entity→algorithm map). The old `update`/`manual` states and
 * the on-load recompute are retired — saved values are the source of truth.
 */
function maskingInventoryRowStatus({ saved, algorithm, recommendedAlgorithm }) {
  if (!saved) return 'new';
  const rec = recommendedAlgorithm != null ? String(recommendedAlgorithm).trim() : '';
  const cur = algorithm != null ? String(algorithm).trim() : '';
  if (rec !== cur) return 'differs';
  return '';
}

function hasFindingEntries(findings) {
  if (findings == null) return false;
  if (typeof findings === 'object' && !Array.isArray(findings)) return Object.keys(findings).length > 0;
  if (Array.isArray(findings)) return findings.length > 0;
  return Boolean(findings);
}

/** Discover/inventory APIs may return findings as an array, a keyed object, or a single value. */
function normalizeFindingsList(findings) {
  if (findings == null) return [];
  if (Array.isArray(findings)) {
    return findings
      .map((f) => {
        if (f == null) return null;
        if (typeof f === 'string' || typeof f === 'number') return String(f);
        if (typeof f === 'object' && !Array.isArray(f)) {
          const k = Object.keys(f)[0];
          return k != null ? String(k) : null;
        }
        return String(f);
      })
      .filter(Boolean);
  }
  if (typeof findings === 'object') return Object.keys(findings);
  return [String(findings)];
}

/** High-sensitivity classifications render as danger tags; everything else stays neutral. */
function isHighSensitivityEntity(label) {
  return /CREDIT[_ ]?CARD|SSN|SOCIAL[_ ]?SECURITY/i.test(String(label || ''));
}

/**
 * Ordered rows for display: { label, count }. Highest count first.
 */
function getFindingsDisplayRows(findings) {
  if (findings == null) return [];
  if (typeof findings === 'object' && !Array.isArray(findings)) {
    return Object.entries(findings)
      .map(([code, v]) => ({ label: String(code), count: Number(v) || 0 }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }
  if (Array.isArray(findings)) {
    const labels = normalizeFindingsList(findings);
    const n = labels.length;
    if (n === 0) return [];
    return labels.map((label, i) => ({ label, count: n - i }));
  }
  return [{ label: String(findings), count: 1 }];
}

function FindingsColumn({ findings }) {
  const rows = getFindingsDisplayRows(findings);
  return (
    <div>
      {rows.map((row, i) => (
        <span
          key={`${row.label}-${i}`}
          className={`sg-tag${isHighSensitivityEntity(row.label) ? ' sg-tag--danger' : ''} mr-1 mb-1`}
        >
          {uiLabel(row.label)}
        </span>
      ))}
    </div>
  );
}

function EncryptFieldColumn({ field, table, selectedFields, setSelectedFields }) {
  const checked = selectedFields[table][field].checked;

  const changeHandler = (value) => {
    const selectedFieldsValue = { ...selectedFields[table][field] };
    selectedFieldsValue.checked = value;
    setSelectedFields({
      ...selectedFields,
      [table]: {
        ...selectedFields[table],
        [field]: selectedFieldsValue
      }
    });
  }
  return (
    <Form.Check
      id={table + '-' + field + '-toggle'}
      onClick={() => changeHandler(!checked)}
      type='switch'
      size='lg'
      checked={checked}
    />
  );
}

const getEncryptButtonProps = (status, fetchingData, fieldQty, hasMaskingDriver = true) => {
  const classes = 'mr-2';

  if (fetchingData) {
    return {
      variant: 'outline-secondary',
      icon: null,
      text: 'STARTING',
      disabled: true
    }
  }
  if (fieldQty === 0) {
    return {
      variant: 'outline-secondary',
      icon: <BsExclamationTriangle className={classes} />,
      text: 'NO FIELDS',
      disabled: true
    }
  }
  if (!hasMaskingDriver) {
    return {
      variant: 'outline-secondary',
      icon: <BsExclamationTriangle className={classes} />,
      text: 'Not Available',
      disabled: true,
    };
  }


  if (typeof status === 'string' && status.includes('ERROR')) {
    return {
      variant: 'outline-danger',
      icon: <BsExclamationTriangle className={classes} />,
      text: status,
      disabled: true
    }
  }

  if (
    typeof status === 'string' &&
    (status.includes('TERMINATING') ||
      status.includes('STARTING') ||
      status.includes('BOOTSTRAPPING') ||
      status.includes('RUNNING'))
  ) {
    return {
      text: status,
      disabled: true
    }
  }


  switch(status) {
    case 'unsecured':
    default:
      return {
        variant: 'primary',
        icon: <BsFillUnlockFill className={classes} />,
        text: 'Encrypt'
      }
    case 'ENCRYPTED':
      return {
        variant: 'outline-secondary',
        icon: <BsLockFill className={classes} />,
        text: 'Decrypt'
      }
    case 'ERROR':
      return {
        variant: 'outline-danger',
        icon: <BsExclamationTriangle className={classes} />,
        text: 'Error - Hover for details'
      }
    case 'ENCRYPTING':
      return {
        variant: 'outline-secondary',
        icon: null,
        text: 'Running..',
        disabled: true
      }
    case 'DECRYPTING':
      return {
        variant: 'outline-secondary',
        icon: null,
        text: 'Running..',
        disabled: true
      }
  }
}

function getEncryptActionHandler(status) {
  if (status === 'ENCRYPTED') {
    return startDecryptJobWithSavedInventory;
  }
  return async () => {};
}

function getSelectedFieldsCount(selectedFields) {
  let result = 0;

  for (const table in selectedFields) {
    const fields = selectedFields[table];

    for (const field in fields) {
      if (fields[field].checked) result++;
    }
  }

  return result;
}

/** Total field rows across a descriptor (table → field). Used to detect when filtering narrows. */
function getDescriptorRowCount(selectedFields) {
  let n = 0;
  for (const t in (selectedFields || {})) {
    n += Object.keys(selectedFields[t] || {}).length;
  }
  return n;
}

const getEncryptedFieldsCount = (encryptedFields) => encryptedFields.length;

export {
  DetailsColumn,
  EncryptFieldColumn,
  FindingsColumn,
  entityToAlgorithmId,
  getAjaxHandler,
  getEncryptActionHandler,
  getEncryptFieldsTableData,
  getEncryptedFieldsCount,
  getEncryptButtonProps,
  getFetchHandler,
  getSecureDataVis,
  getSecureLandscapeVis,
  getCoverageGaugeData,
  getSelectedFieldsCount,
  getDescriptorRowCount,
  hasFindingEntries,
  maskingInventoryRowStatus,
  recommendedAlgorithmFromFindings,
  maskingAlgorithmFromEntityMap,
  resolveRecommendedMaskingAlgorithm,
  DEFAULT_MASKING_ALGORITHM,
  topFindingEntity
};